import { DtmfPlayer } from 'play-dtmf';
import { URI, Web } from 'sip.js';
import { SimpleUserOptions } from 'sip.js/lib/platform/web';

import { postWebrtcMetrics, WebrtcMetrics } from '~providers/AsteriskDiallerProvider/api';
import SocketManagerBase from '~services/SocketManagerBase';

import {
  AgentState,
  ChannelAction,
  ServerMessage,
  ServerMessageResponseDecoder,
  UpdateCallDisposition,
} from './domain';
import { extractMediaStatsFromStats, MediaRtpStats } from './rtp-stats';

type Callbacks = {
  open: () => void;
  message: (message: ServerMessage) => void;
  closed: (code: number) => void;
  agentCallConnected: (isConnected: boolean) => void;
};

type CallbacksType = keyof Callbacks;

const noop = () => {};

export class AsteriskDiallerManager extends SocketManagerBase {
  private groupId: number;
  private fullName: string;
  private sipWebsocketAddress: string;
  private sipHost: string;
  private simple: Web.SimpleUser | undefined = undefined;
  private registerTimeout: number = 0;
  private metricsInterval: number | undefined = undefined;
  private shouldReconnect: boolean = true;
  private agentToDiallerAudioStats: MediaRtpStats | null = null;
  private diallerToAgentAudioStats: MediaRtpStats | null = null;
  private callbacks: Callbacks = {
    open: noop,
    message: noop,
    closed: noop,
    // Dunno if this is still needed or not
    agentCallConnected: noop,
  };

  constructor(address: string, groupId: number, fullName: string, sipWebsocketAddress: string, sipHost: string) {
    super('AsteriskDiallerManager', address);
    this.groupId = groupId;
    this.fullName = fullName;
    this.sipWebsocketAddress = sipWebsocketAddress;
    this.sipHost = sipHost;
  }

  /** Connects the predictive dialler socket  */
  public connect(): void {
    this.shouldReconnect = true;

    // Add media container
    if (!this.mediaContainerExist()) {
      this.addMediaContainer();
    }

    // Now run the base connect logic
    super.connect();
  }

  /** Disconnects the predictive dialler socket  */
  public disconnect(): void {
    this.shouldReconnect = false;

    // Clear any possible register timeout events
    clearTimeout(this.registerTimeout);

    // Remove media container
    if (this.mediaContainerExist()) {
      this.removeMediaContainer();
    }

    // destroy webrtc connection
    if (this.simple != null) {
      this.simple.disconnect();
    }

    // Now run the base disconnect logic
    super.disconnect();
  }

  /** Sets the DTMF action */
  public sendDTMF = async (digit: string) => {
    console.log('AsteriskDiallerManager setDTMF digit ', digit);

    if (this.simple === undefined) {
      console.error('sendDTMF: simple missing.');
      return;
    }

    const dtmfPlayer = new DtmfPlayer();

    dtmfPlayer.play(digit);

    setTimeout(() => {
      dtmfPlayer.stop();
      dtmfPlayer.close();
    }, 200);

    try {
      await this.simple.sendDTMF(digit);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  /** Initiates an external conference */
  public initiateExternalConference = (endpoint: string) => {
    console.log('AsteriskDiallerManager initiate external conference with endpoint ', endpoint);

    if (this.ws === undefined) {
      console.error('initiateExternalConference: websocket missing.');
      return;
    }

    const req = {
      requestExternalConference: {
        endpoint,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Initiates an internal conference */
  public initiateInternalConference = (username: string) => {
    console.log('AsteriskDiallerManager initiate internal conference with agent ', username);

    if (this.ws === undefined) {
      console.error('initiateExternalConference: websocket missing.');
      return;
    }

    const req = {
      requestInternalConference: {
        agent: username,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Removes an existing internal conference articipant from the call */
  public removeInternalConferenceParticipant = (username: string) => {
    console.log(`AsteriskDiallerManager remove ${username} from internal conference`);

    if (this.ws === undefined) {
      console.error('removeInternalConferenceAgent: websocket missing.');
      return;
    }

    const req = {
      removeFromInternalConference: {
        agent: username,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Triggers an action on a customer channel */
  public customerChannelAction = (channelKey: string, action: ChannelAction) => {
    console.log(`AsteriskDiallerManager trigger action '${action}' for channel ${channelKey}`);

    if (this.ws === undefined) {
      console.error('channelAction: websocket missing.');
      return;
    }

    const req = {
      requestChannelAction: {
        chan: channelKey,
        action: action,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Sets the agents state */
  public setState = (state: string) => {
    console.log('AsteriskDiallerManager setting agent state to ', state);

    if (this.ws === undefined) {
      console.error('setState: websocket missing.');
      return;
    }

    // Why Sep/ RJ (╯°□°)╯︵ ┻━┻ guess we are stuck with this for now
    const nextStatus =
      state === 'Available' ? AgentState.InQueue : state === 'Not Ready' ? AgentState.Unavailable : state;

    const req = {
      requestState: {
        nextState: nextStatus,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Changes the agents current group */
  public changeGroup = (groupId: number, diallerType: string, campaignType: string) => {
    console.log('AsteriskDiallerManager changing agent group to ', groupId);

    const req = {
      setGroup: {
        groupId: groupId,
        diallerType: diallerType,
        campaignType: campaignType,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Enables agent to preview a call */
  public previewCall = (callId: string) => {
    console.log('AsteriskDiallerManager previewing call ', callId);

    if (this.ws === undefined) {
      console.error('previewCall: websocket missing.');
      return;
    }

    const req = {
      originatePreviewCall: {
        callId,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Enables the agent to leave their current call */
  public leaveCall = (callId: string) => {
    console.log('AsteriskDiallerManager leaving call ', callId);

    if (this.ws === undefined) {
      console.error('leaveCall: websocket missing.');
      return;
    }

    const req = {
      requestLeaveCall: {
        callId: callId,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Rejects the incoming call */
  public rejectCall = (callId: string) => {
    console.log('AsteriskDiallerManager rejecting call ', callId);

    if (this.ws === undefined) {
      console.error('rejectCall: websocket missing.');
      return;
    }

    const req = {
      rejectInternalConference: {
        callId: callId,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Accepts the incoming call */
  public acceptCall = (callId: string) => {
    console.log('AsteriskDiallerManager accepting call ', callId);

    if (this.ws === undefined) {
      console.error('acceptCall: websocket missing.');
      return;
    }

    const req = {
      acceptInternalConference: {
        callId: callId,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Places a manual outbound call (placeManualOutboundCall in App.svelte) */
  public placeManualOutboundCall = (endpoint: string, timezone: string) => {
    console.log('AsteriskDiallerManager placing manual outbound call to ', endpoint);

    if (this.ws === undefined) {
      console.error('placeManualOutboundCall: websocket missing.');
      return;
    }

    const req = {
      requestManualOutbound: {
        endpoint,
        lead_name: endpoint,
        external_id: endpoint,
        timezone,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Sets the call disposition */
  public setCallDisposition = async (disposition: UpdateCallDisposition) => {
    console.log('AsteriskDiallerManager set call disposition');

    if (this.ws === undefined) {
      console.error('setCallDisposition: websocket missing.');
      return;
    }

    let req: any = {
      setCallDisposition: {
        disposition_code: disposition.code,
        disposition_sub_code: disposition.subCode,
        callback_time: disposition.callback?.scheduled ?? null,
        callback_to_agent: disposition.callback?.forAgent ?? null,
        callback_endpoint: disposition.callback?.endpoint ?? null,
        callback_notes: disposition.callback?.notes ?? null,
        exclusion_endpoint: disposition.exclusion?.endpoint ?? null,
      },
    };

    if (disposition.exclusion) {
      req.setCallDisposition.exclusion_list = disposition.exclusion.list;
    }

    // Anonymous caller fields (App.svelte:1059-1062)
    if (disposition.leadName !== undefined) {
      req.setCallDisposition.lead_name = disposition.leadName;
      req.setCallDisposition.lead_timezone = disposition.leadTimezone;
      req.setCallDisposition.lead_external_id = disposition.leadExternalId;
    }

    this.send(JSON.stringify(req));
  };

  /** Sends HCF/Amplifon custom attributes to the server (App.svelte:1080-1126) */
  public setContactAttributes = (callId: string, attributes: { [key: string]: string }) => {
    if (this.ws === undefined) {
      console.error('setContactAttributes: websocket missing.');
      return;
    }

    const req = {
      setContactAttributes: {
        callId,
        attributes,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Sets the call recording as enabled or disabled  */
  public setRecording = (recording: boolean) => {
    console.log('AsteriskDiallerManager set recording ', recording);

    if (this.ws === undefined) {
      console.error('setRecording: websocket missing.');
      return;
    }

    const req = {
      pauseResumeRecording: {
        recording,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Sets the agent as either muted or unmuted */
  public setMute = (muted: boolean) => {
    console.log('AsteriskDiallerManager set agent as muted ', muted);

    if (this.ws === undefined) {
      console.error('setMute: websocket missing.');
      return;
    }

    const req = {
      setMutedMicrophone: {
        muted,
      },
    };

    this.send(JSON.stringify(req));
  };

  /** Event that is triggered on the successful connection of the websocket */
  protected onOpen = () => {
    if (this.ws === undefined) {
      console.error('onOpen: websocket missing.');
      return;
    }

    const callReq = {
      provision_endpoint: true,
      group_id: this.groupId,
      fullName: this.fullName,
    };

    this.send(JSON.stringify(callReq));

    this.callbacks.open();
  };

  /** Event handler that manages all responses from the connected websocket */
  protected onMessage = (messageEvent: MessageEvent): void => {
    let msg: ServerMessage;

    try {
      const rawMessage = JSON.parse(messageEvent.data);
      msg = ServerMessageResponseDecoder.runWithException(rawMessage);
    } catch (e) {
      console.error('onMessage: Unable to decode message payload: ', e);
      return;
    }

    if (msg.asteriskAuthentication) {
      // Clear any previous registered timeout event, so we do not bubble
      clearTimeout(this.registerTimeout);
      const auth = msg.asteriskAuthentication;
      // give it 1 second before proceeding
      this.registerTimeout = window.setTimeout(
        () => this.registerSip(this.sipWebsocketAddress, this.sipHost, auth.asteriskUsername, auth.asteriskPassword),
        1000,
      );
      return;
    }

    this.callbacks.message(msg);
  };

  /** Event handler that is triggered if the websocket is closed/ ended */
  protected onClose = (closeEvent: CloseEvent): void => {
    console.log('AsyncStatsManager websocket closed with code: ', closeEvent.code);

    this.ws = undefined;
    this.callbacks.closed(closeEvent.code);

    // Auto-reconnect after 1 second unless disconnect() was called explicitly
    if (this.shouldReconnect) {
      console.log('AsteriskDiallerManager: reconnecting in 1 second...');
      setTimeout(() => this.connect(), 1000);
    }
  };

  public on = <T extends CallbacksType>(listener: T, action: Callbacks[T]) => {
    // A bit dodgy, but we set all callbacks as a noop so this will cover the case where someone
    // makes a typo and ignores the typescript linter message, as typescript will still compile and run etc.
    if (this.callbacks[listener]) {
      this.callbacks[listener] = action;
    }
  };

  /** Adds the media container from the DOM */
  private addMediaContainer = () => {
    const remoteVideo = document.createElement('video');
    remoteVideo.id = 'remoteVideo';
    const localVideo = document.createElement('video');
    localVideo.id = 'localVideo';
    localVideo.muted = true;
    const container = document.createElement('div');
    container.id = 'predictive-dialler-media-container';
    container.style.display = 'none';

    container.appendChild(remoteVideo);
    container.appendChild(localVideo);
    document.body.appendChild(container);
  };

  /** Checks if the media container exists in the DOM */
  private mediaContainerExist = () => {
    return Boolean(document.getElementById('predictive-dialler-media-container'));
  };

  /** Removes the media container from the DOM */
  private removeMediaContainer = () => {
    const container = document.getElementById('predictive-dialler-media-container');

    if (container) {
      container.remove();
    }
  };

  /** Register the agent as a sip user */
  private registerSip = (address: string, host: string, username: string, password: string) => {
    // Clean up previous SimpleUser to prevent zombie SIP sessions
    if (this.simple) {
      console.log('registerSip: cleaning up previous SimpleUser');
      this.simple.disconnect().catch((reason) => {
        console.log('unable to disconnect previous SimpleUser', reason);
      });
      this.simple = undefined;
    }
    const options: SimpleUserOptions = {
      delegate: {
        onRegistered: () => {
          console.log('AsteriskDiallerManager softphone registered');

          // get asterisk to call us now
          const callReq = {
            requestCall: {
              agentAddress: 'PJSIP/' + username,
              connectDTMF: '',
            },
          };

          this.send(JSON.stringify(callReq));
          console.log('AsteriskDiallerManager call requested');
        },
        onServerConnect: async () => {
          if (!this.simple) {
            console.error('onServerConnect: simple missing');
            return;
          }

          console.log('AsteriskDiallerManager softphone connected');

          await this.simple
            .register()
            .then()
            .catch((reason) => {
              console.log('AsteriskDiallerManager unable to register', reason);
            });
        },
        onCallReceived: () => {
          if (!this.simple) {
            console.error('onCallReceived: simple missing');
            return;
          }

          const simpleId = this.simple.id;

          console.log(`AsteriskDiallerManager [${simpleId}] call received`);

          this.simple.answer().catch((error) => {
            console.error(`onCallReceived [${simpleId}] failed to answer call`);
            console.error(error);
            alert(`[${simpleId}] Failed to answer call.\n` + error);
          });
        },
        onCallAnswered: () => {
          this.callbacks.agentCallConnected(true);
          console.log('AsteriskDiallerManager agent call connected');
        },
        onCallHangup: () => {
          this.callbacks.agentCallConnected(false);
          console.log('AsteriskDiallerManager agent call disconnected');
        },
        onUnregistered: () => {
          this.callbacks.agentCallConnected(false);
          console.log('AsteriskDiallerManager agent call unregistered');
        },
        /*
        // https://github.com/onsip/SIP.js/blob/96071cead9153c180afcf4dd70288cf3a85bd73d/docs/simple-user/sip.js.simpleuserdelegate.md
        // for most of the disconnected events, we just wait for asterisk to tell us something is wrong.
            onCallAnswered()	Called when a call is answered.
            onCallCreated()	Called when a call is created.
            onCallDTMFReceived(tone, duration)	Called when a call receives an incoming DTMF tone.
            onCallHangup()	Called when a call is hung up.
            onCallHold(held)	Called when a call is put on hold or taken off hold.
            onCallReceived()	Called when a call is received.
            onMessageReceived(message)	Called upon receiving a message.
            onRegistered()	Called when user is registered to received calls.
            onServerConnect()	Called when user is connected to server.
            onServerDisconnect(error)	Called when user is no longer connected.
            onUnregistered()	Called when user is no longer registered to received calls.

        */
      },
      media: {
        local: {
          video: (document.getElementById('localVideo') as HTMLVideoElement) || undefined,
        },
        remote: {
          // NOTE: Chrome did not like having video connected....
          // video: document.getElementById('remoteVideo'),
          // This is necessary to do an audio/video call as opposed to just a video call
          audio: (document.getElementById('remoteVideo') as HTMLAudioElement) || undefined,
        },
      },
      userAgentOptions: {
        uri: new URI('sip', username, host),
        authorizationUsername: username,
        authorizationPassword: password,
        displayName: username,
        // 'stunServers': ["stun:stun.l.google.com:19302"]
      },
    };

    this.simple = new Web.SimpleUser(address, options);
    this.simple.sendDTMF = function (tone: string) {
      // @ts-ignore
      this.logger.log(`[${this.id}] sending DTMF...`);
      if (!/^[0-9A-D#*,]$/.exec(tone)) {
        return Promise.reject(new Error('Invalid DTMF tone.'));
      }
      // @ts-ignore
      if (!this.session) {
        return Promise.reject(new Error('Session does not exist.'));
      }
      // https://tools.ietf.org/html/draft-kaplan-dispatch-info-dtmf-package-00#section-5.3
      // @ts-ignore
      this.logger.log(`[${this.id}] Sending DTMF tone: ${tone}`);
      const dtmf = tone;
      const duration = 100;
      const body = {
        contentDisposition: 'render',
        contentType: 'application/dtmf-relay',
        content: 'Signal=' + dtmf + '\r\nDuration=' + duration,
      };
      const requestOptions = { body };

      // @ts-ignore
      return this.session.info({ requestOptions }).then(() => {
        return;
      });
    };

    this.simple.connect().catch((reason) => {
      console.log('unable to connect', reason);
    });
  };

  /** Starts 5-second metrics polling (call on Connected state) */
  public startMetricsPolling = (contactId: string) => {
    if (this.metricsInterval !== undefined) return;

    this.metricsInterval = window.setInterval(() => {
      this.updateAndPostMetrics(contactId);
    }, 5_000);
  };

  /** Stops metrics polling and resets stats (call on AfterCallWork state) */
  public stopMetricsPolling = () => {
    if (this.metricsInterval !== undefined) {
      clearInterval(this.metricsInterval);
      this.metricsInterval = undefined;
    }
    this.agentToDiallerAudioStats = null;
    this.diallerToAgentAudioStats = null;
  };

  // NOTE: Metrics polling was unable to be verified in local WSL2 environment because RTP media does not flow (RTP_TIMEOUT=0). peerConnection.getStats() returns empty audio stats, so POST requests are never sent. This must be tested in a QA environment where real RTP media is present.
  private updateAndPostMetrics = (contactId: string) => {
    if (!contactId) {
      console.error('Unable to post call metrics: contactId is undefined');
      return;
    }

    if (!this.simple) {
      console.error('Unable to post call metrics: SIP session missing');
      return;
    }

    const session = this.simple as any;
    const peerConnection = session?.session?.sessionDescriptionHandler?.peerConnection as RTCPeerConnection | undefined;

    if (!peerConnection) {
      console.error('Unable to post call metrics: peer connection missing');
      return;
    }

    const prevAgentStats = this.agentToDiallerAudioStats;
    const prevDiallerStats = this.diallerToAgentAudioStats;

    peerConnection
      .getStats()
      .then((rawStats) => {
        const timestamp = new Date();

        this.agentToDiallerAudioStats = extractMediaStatsFromStats(timestamp, rawStats, 'audio_input');
        this.diallerToAgentAudioStats = extractMediaStatsFromStats(timestamp, rawStats, 'audio_output');

        if (this.agentToDiallerAudioStats) {
          const stats = this.getTimeSeriesStats(
            this.agentToDiallerAudioStats,
            prevAgentStats,
            'audio-agent-to-dialler',
          );
          this.postCallMetrics(contactId, stats).catch((err) =>
            console.error('Unable to post agent to dialler audio stats: ', err),
          );
        }

        if (this.diallerToAgentAudioStats) {
          const stats = this.getTimeSeriesStats(
            this.diallerToAgentAudioStats,
            prevDiallerStats,
            'audio-dialler-to-agent',
          );
          this.postCallMetrics(contactId, stats).catch((err) =>
            console.error('Unable to post dialler to agent audio stats: ', err),
          );
        }
      })
      .catch((err) => {
        console.error('Unable to get peer connection raw stats: ', err);
      });
  };

  // Delta calculation between current and previous stats (getTimeSeriesStats in App.svelte)
  private getTimeSeriesStats = (current: MediaRtpStats, previous: MediaRtpStats | null, streamType: string) => {
    let packetsLostDelta = current.packetsLost ?? 0;
    let packetsCountDelta = current.packetsCount ?? 0;

    if (previous && current) {
      packetsLostDelta =
        (current.packetsLost ?? 0) > (previous.packetsLost ?? 0)
          ? (current.packetsLost ?? 0) - (previous.packetsLost ?? 0)
          : 0;
      packetsCountDelta =
        (current.packetsCount ?? 0) > (previous.packetsCount ?? 0)
          ? (current.packetsCount ?? 0) - (previous.packetsCount ?? 0)
          : 0;
    }

    return {
      timestamp: current.timestamp,
      packetsLost: current.packetsLost,
      packetsLostDelta,
      packetsCount: current.packetsCount,
      packetsCountDelta,
      softphoneStreamType: streamType,
      jitterBufferMillis: Math.ceil(current.jbMilliseconds ?? 0),
      roundTripTimeMillis: Math.ceil(current.rttMilliseconds ?? 0),
    };
  };

  private postCallMetrics = async (contactId: string, data: ReturnType<typeof this.getTimeSeriesStats>) => {
    const metrics: WebrtcMetrics = {
      callId: contactId,
      snapshotTimestamp: data.timestamp,
      jitterBufferMillis: data.jitterBufferMillis,
      roundTripTimeMillis: data.roundTripTimeMillis,
      packetsLost: data.packetsLostDelta,
      packetsCount: data.packetsCountDelta,
      streamType: data.softphoneStreamType,
    };

    return postWebrtcMetrics(metrics);
  };
}

export default AsteriskDiallerManager;
